import { AvatarGroup } from '@mui/material';
import { BoxProps } from '@mui/system';
import parse from 'html-react-parser';
import { ReactElement, SyntheticEvent } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import TruncateMarkup from 'react-truncate-markup';

import { ReactComponent as CalendarIcon } from 'assets/icons/calendar2.svg';
import { ReactComponent as CommentsIcon } from 'assets/icons/comments2.svg';
import { Avatar } from 'components';
import { MONTH_DAY_SHORT } from 'constants/index';
import { Links } from 'settings';
import { theme } from 'styles';
import { InViewItemRef, TaskListItem as TaskListItemType } from 'types';
import { formattedDate } from 'utils';

import {
  AvatarW,
  CountW,
  DateContainer,
  DateW,
  DescriptionW,
  FooterLeftW,
  FooterW,
  IconW,
  PriorityChip,
  ProfileHeaderContentW,
  ProfileHeaderW,
  ProgressBarContainer,
  ProgressFill,
  ProgressLabel,
  ProgressTrack,
  TaskW,
  TitleW,
} from './styled';
import { avatarGroupStyles, avatarWrapperStyles } from './styles';
import { getAssigneeInitials } from './utils';

interface TaskListItemProps extends TaskListItemType {
  isPreview?: boolean;
  isProfile?: boolean;
  listItemSx?: BoxProps['sx'];
  lastItemRef?: InViewItemRef;
}

export function TaskListItem({
  title,
  deadline,
  assignee,
  description,
  isPreview = false,
  isProfile = false,
  listItemSx,
  lastItemRef,
  id,
  comments_count,
  progress,
  priority,
}: TaskListItemProps): ReactElement {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isProfilePage = pathname.includes(Links.profile.index);

  const detailsLink = generatePath(
    isProfilePage ? Links.profile.taskManagement.taskDetails : Links.we.taskManagement.taskDetails,
    { taskId: id },
  );

  const progressValue = progress ?? 0;

  function handleOnTaskClick() {
    navigate(detailsLink);
  }

  function handleAvatarClick(e: SyntheticEvent) {
    e.stopPropagation();
  }

  const commentIconColor = comments_count ? theme.palette.info.main : theme.palette.primary.p030;

  const avatarSection = assignee?.length ? (
    <AvatarW>
      <AvatarGroup max={3} sx={avatarGroupStyles}>
        {assignee.map((user) => {
          const assigneeInitials = getAssigneeInitials(user);
          return (
            <Avatar
              key={user.id}
              size="s"
              userId={user.id}
              filename={user?.avatar?.small}
              initials={assigneeInitials}
              sx={{
                width: 40,
                height: 40,
                ...(assigneeInitials && { background: theme.palette.info.main }),
              }}
              wrapperSx={avatarWrapperStyles}
              onClick={handleAvatarClick}
            />
          );
        })}
      </AvatarGroup>
    </AvatarW>
  ) : (
    <AvatarW></AvatarW>
  );

  const footerContent = (
    <FooterW isPreview={isPreview}>
      <FooterLeftW>
        {deadline && (
          <DateContainer>
            <CalendarIcon style={{ color: '#fff', fill: '#fff', height: '24px', width: '24px' }} />
            <DateW>{formattedDate(new Date(deadline), MONTH_DAY_SHORT)}</DateW>
          </DateContainer>
        )}
        <DateContainer>
          <IconW>
            {comments_count ? (
              <CommentsIcon style={{ color: commentIconColor, fill: commentIconColor }} />
            ) : (
              <CommentsIcon style={{ color: commentIconColor, fill: commentIconColor }} />
            )}
            {comments_count ? <CountW>{comments_count}</CountW> : null}
          </IconW>
        </DateContainer>
      </FooterLeftW>
      {avatarSection}
    </FooterW>
  );

  if (isProfile) {
    return (
      <TaskW isPreview={isPreview} sx={listItemSx} ref={lastItemRef} onClick={handleOnTaskClick}>
        <ProfileHeaderW>
          <ProfileHeaderContentW>
            <TitleW isPreview={isPreview}>{title}</TitleW>
            {description && (
              <TruncateMarkup lines={2} ellipsis={<span>...</span>}>
                <DescriptionW>{parse(description)}</DescriptionW>
              </TruncateMarkup>
            )}
          </ProfileHeaderContentW>
          {priority && <PriorityChip priority={priority}>{priority}</PriorityChip>}
        </ProfileHeaderW>
        <ProgressBarContainer>
          <ProgressTrack>
            <ProgressFill value={progressValue} />
          </ProgressTrack>
          <ProgressLabel>{progressValue}%</ProgressLabel>
        </ProgressBarContainer>

        {footerContent}
      </TaskW>
    );
  }

  return (
    <TaskW
      isPreview={isPreview}
      sx={{ ...listItemSx }}
      ref={lastItemRef}
      onClick={handleOnTaskClick}
    >
      <ProfileHeaderW>
        <ProfileHeaderContentW>
          <TitleW isPreview={isPreview}>{title}</TitleW>
          {description && (
            <TruncateMarkup lines={1} ellipsis={<span>...</span>}>
              <DescriptionW>{parse(description)}</DescriptionW>
            </TruncateMarkup>
          )}
        </ProfileHeaderContentW>
        {priority && <PriorityChip priority={priority}>{priority}</PriorityChip>}
      </ProfileHeaderW>

      <ProgressBarContainer>
        <ProgressTrack>
          <ProgressFill value={progressValue} />
        </ProgressTrack>
        <ProgressLabel>{progressValue}%</ProgressLabel>
      </ProgressBarContainer>

      {(deadline || comments_count !== undefined || assignee) && (
        <FooterW isPreview={isPreview}>
          <FooterLeftW>
            {deadline && (
              <DateContainer>
                <CalendarIcon />
                <DateW>{formattedDate(new Date(deadline), MONTH_DAY_SHORT)}</DateW>
              </DateContainer>
            )}
            <DateContainer>
              <IconW>
                {comments_count ? (
                  <CommentsIcon style={{ color: commentIconColor, fill: commentIconColor }} />
                ) : (
                  <CommentsIcon style={{ color: commentIconColor, fill: commentIconColor }} />
                )}
                {comments_count ? <CountW>{comments_count}</CountW> : null}
              </IconW>
            </DateContainer>
          </FooterLeftW>
          <>{avatarSection}</>
        </FooterW>
      )}
    </TaskW>
  );
}
